
import { useState, useCallback, useMemo } from 'react'
import { Cell, CELL_SIZE } from './Cell.js'
import { ShipOverlay } from './ShipOverlay.js'
import { SplashFX } from '../FX/SplashFX.js'
import { HitFlashFX } from '../FX/HitFlashFX.js'
import { TorpedoFX } from '../FX/TorpedoFX.js'
import { SonarPingFX } from '../FX/SonarPingFX.js'
import { ShipSunkFX } from '../FX/ShipSunkFX.js'
import { SonarBadge } from '../FX/SonarBadge.js'
import { useAnimationStore } from '../../store/animationStore.js'
import type { CellState, Coord, Ship, TerrainType } from '@fleetduel/shared'
import { COLS, ROWS, coordKey, colIndex, getShipCells, getSonarZone } from '@fleetduel/shared'

interface GridProps {
  cellStates: Map<string, CellState>
  isOwnBoard: boolean
  ships?: Ship[]
  sonarZone?: Set<string>
  previewCells?: Map<string, 'valid' | 'invalid'>
  /** Blocked terrain cells keyed by coord key (GDD-v3 §4) */
  terrainCells?: Map<string, TerrainType>
  /** Theater water tint overlaid on every water tile */
  waterTint?: string
  onCellClick?: (coord: Coord) => void
  onCellHover?: (coord: Coord) => void
  onGridLeave?: () => void
  onContextMenu?: (e: React.MouseEvent) => void
}

export const HEADER_LEFT = 28
export const HEADER_TOP = 22

/** Convert a grid coord to pixel position relative to the grid container (including headers) */
function coordToPixel(coord: Coord): { x: number; y: number } {
  return {
    x: HEADER_LEFT + colIndex(coord.col) * CELL_SIZE,
    y: HEADER_TOP + (coord.row - 1) * CELL_SIZE,
  }
}

export function Grid({
  cellStates,
  isOwnBoard,
  ships = [],
  sonarZone,
  previewCells,
  terrainCells,
  waterTint,
  onCellClick,
  onCellHover,
  onGridLeave,
  onContextMenu,
}: GridProps) {
  const current = useAnimationStore(s => s.current)
  const sonarBadges = useAnimationStore(s => s.sonarBadges)
  const resolve = useAnimationStore(s => s.resolve)
  const [shaking, setShaking] = useState(false)

  // Cells currently being animated (delay their marker rendering)
  const animatingCells = useMemo(() => {
    if (!current) return new Set<string>()
    const isOnThisBoard = current.isOwnBoard === isOwnBoard
    if (!isOnThisBoard) return new Set<string>()
    const keys = new Set<string>()
    keys.add(coordKey(current.target))
    if (current.cells) {
      for (const c of current.cells) keys.add(coordKey(c.coord))
    }
    return keys
  }, [current, isOwnBoard])

  const shipCells = new Set<string>()
  for (const ship of ships) {
    for (const cell of getShipCells(ship)) {
      shipCells.add(coordKey(cell))
    }
  }

  const handleShake = useCallback(() => {
    setShaking(true)
    setTimeout(() => setShaking(false), 150)
  }, [])

  // Determine if we should render FX for the current animation on THIS grid
  const renderFX = current && (current.isOwnBoard === isOwnBoard)

  // Build FX layer
  let fxElement: React.ReactNode = null
  if (renderFX && current) {
    const pos = coordToPixel(current.target)

    switch (current.type) {
      case 'strike-miss':
        fxElement = (
          <SplashFX x={pos.x} y={pos.y} onDone={resolve} />
        )
        break

      case 'strike-hit':
        fxElement = (
          <HitFlashFX x={pos.x} y={pos.y} onShake={handleShake} onDone={resolve} />
        )
        break

      case 'torpedo':
        if (current.cells && current.cells.length > 0) {
          const p1 = coordToPixel(current.cells[0].coord)
          const p2 = current.cells.length > 1 ? coordToPixel(current.cells[1].coord) : undefined
          fxElement = (
            <TorpedoFX
              x1={p1.x}
              y1={p1.y}
              x2={p2?.x}
              y2={p2?.y}
              cells={current.cells}
              direction={current.direction || 'horizontal'}
              onCellImpact={() => {}}
              onDone={resolve}
            />
          )
        }
        break

      case 'sonar':
        if (current.sonarCount !== undefined) {
          const zone = getSonarZone(current.target)
          const zonePositions = zone.map(c => coordToPixel(c))
          fxElement = (
            <SonarPingFX
              x={pos.x}
              y={pos.y}
              zoneCellPositions={zonePositions}
              count={current.sonarCount}
              onDone={resolve}
            />
          )
        }
        break

      case 'ship-sunk':
        if (current.sunkShip) {
          fxElement = (
            <ShipSunkFX
              x={pos.x}
              y={pos.y}
              shipType={current.sunkShip}
              onDone={resolve}
            />
          )
        }
        break
    }
  }

  // Build sonar badge elements
  const badgeElements = Array.from(sonarBadges.entries()).map(([key, count]) => {
    // Parse coord key back to coord for positioning
    const col = key[0] as Coord['col']
    const row = parseInt(key[1]) as Coord['row']
    const pos = coordToPixel({ col, row })
    return <SonarBadge key={`badge-${key}`} x={pos.x} y={pos.y} count={count} />
  })

  return (
    <div
      onMouseLeave={onGridLeave}
      onContextMenu={onContextMenu}
      style={{
        position: 'relative',
        display: 'inline-block',
        animation: shaking ? 'screen-shake 150ms linear' : undefined,
      }}
    >
      {/* Column headers */}
      <div style={{ display: 'flex', marginLeft: HEADER_LEFT, marginBottom: 2 }}>
        {COLS.map(col => (
          <div key={col} style={{
            width: CELL_SIZE,
            textAlign: 'center',
            fontSize: 12,
            color: '#8DA9C4',
            fontWeight: 600,
          }}>
            {col}
          </div>
        ))}
      </div>

      {/* Grid rows */}
      {ROWS.map(row => (
        <div key={row} style={{ display: 'flex' }}>
          <div style={{
            width: HEADER_LEFT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            color: '#8DA9C4',
            fontWeight: 600,
          }}>
            {row}
          </div>

          {COLS.map(col => {
            const coord: Coord = { col, row }
            const key = coordKey(coord)
            const state = cellStates.get(key) || 'unknown'
            const isBeingAnimated = animatingCells.has(key)

            return (
              <Cell
                key={key}
                coord={coord}
                state={isBeingAnimated ? 'unknown' : state}
                isOwnBoard={isOwnBoard}
                hasShip={shipCells.has(key)}
                isInSonarZone={sonarZone?.has(key) || false}
                previewState={previewCells?.get(key)}
                terrain={terrainCells?.get(key)}
                waterTint={waterTint}
                onClick={onCellClick}
                onHover={onCellHover}
              />
            )
          })}
        </div>
      ))}

      {/* Ship SVG overlays (own board only) */}
      {isOwnBoard && ships.length > 0 && (
        <ShipOverlay
          ships={ships}
          headerOffsetLeft={HEADER_LEFT}
          headerOffsetTop={HEADER_TOP}
        />
      )}

      {/* FX animation layer */}
      {fxElement}

      {/* Persistent sonar badges */}
      {!isOwnBoard && badgeElements}
    </div>
  )
}
